import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { cn, getInitials, Person } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  EllipsisVertical,
  LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, Users } from "lucide-react";
import { CommandList } from "cmdk";
import { Link } from "react-router-dom";
import { Throbber } from "@lytra/frontend-common";

export interface Action {
  id: string;
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  loading?: boolean;
}

export interface InfoField {
  id: string;
  label: string;
  value?: string;
  valueId?: string;
  icon?: LucideIcon;
  options?: {
    value: string;
    label: string;
    /** A team row of the assignee picker (#960). */
    team?: boolean;
    /** A person listed under the team row above it. */
    nested?: boolean;
  }[];
  onSelect?: (value: string) => void;
}

export interface DetailField {
  icon: LucideIcon;
  label?: string;
  value: string | null | undefined;
  hint?: string;
}

export interface DetailSection {
  title?: string;
  entries: DetailField[];
}

export interface ConnectorHealthInfo {
  sourceType: string;
  connectionStatus?: string | null;
  isConnected: boolean;
  errorMessage?: string | null;
  lastSyncedAt?: string | null;
}

interface ConversationHeaderProps {
  person?: Person;
  /** When set, the person's name links to their customer page (e.g. /contacts/:id). */
  personHref?: string;
  isOnline?: boolean;
  title?: string | null | undefined;
  summary: string | null | undefined;
  actions: Action[];
  fields: InfoField[];
  /** Live connector state for this conversation's source; omit/null when healthy or not applicable. */
  connectorHealth?: ConnectorHealthInfo | null;
  detailSection1?: DetailSection;
  detailSection2?: DetailSection;
  detailSection3?: DetailSection;
  /** Shown above the surrounding columns, whose slot draws the dividing line. */
  standalone?: boolean;
}

export default function ConversationHeader({
  person,
  personHref,
  isOnline,
  title,
  summary,
  actions,
  fields,
  connectorHealth,
  detailSection1,
  detailSection2,
  detailSection3,
  standalone = false,
}: ConversationHeaderProps) {
  const [isInfoExpanded, setIsInfoExpanded] = useState(() => {
    const savedState = localStorage.getItem("conversationHeaderExpanded");
    const hasContent = Boolean(
      summary ?? detailSection1 ?? detailSection2 ?? detailSection3,
    );
    if (!hasContent) return false;

    return savedState !== null
      ? (JSON.parse(savedState) as boolean)
      : hasContent;
  });

  useEffect(() => {
    localStorage.setItem(
      "conversationHeaderExpanded",
      JSON.stringify(isInfoExpanded),
    );
  }, [isInfoExpanded]);

  const [visibleActions, setVisibleActions] = useState<string[]>([]);
  const actionContainerRef = useRef<HTMLDivElement>(null);
  const [actionsLabelWidth, setActionsLabelWidth] = useState(0);
  const [buttonWidths, setButtonWidths] = useState<number[]>([]);
  const [isInitialMeasurementDone, setIsInitialMeasurementDone] =
    useState(false);

  const measureButtons = useCallback(() => {
    const label = actionContainerRef.current?.querySelector(".actions-label");
    if (label) {
      setActionsLabelWidth((label as HTMLSpanElement).offsetWidth);
    }

    const buttons =
      actionContainerRef.current?.querySelectorAll(".action-button");
    if (buttons) {
      const widths = Array.from(buttons).map(
        (button) => (button as HTMLButtonElement).offsetWidth,
      );
      setButtonWidths(widths);
      setIsInitialMeasurementDone(true);
    }
  }, []);

  useEffect(() => {
    if (actions.length > 0 && !isInitialMeasurementDone) {
      setTimeout(measureButtons, 0);
    }
  }, [actions, isInitialMeasurementDone, measureButtons]);

  useEffect(() => {
    const container = actionContainerRef.current;
    if (!container || !isInitialMeasurementDone) return;

    const updateVisibleActions = () => {
      const containerWidth = container.offsetWidth;
      let availableWidth = containerWidth - actionsLabelWidth - 40; // Reserve space for dropdown button
      const newVisibleActions: string[] = [];

      for (let i = 0; i < actions.length; i++) {
        if (availableWidth >= buttonWidths[i]) {
          newVisibleActions.push(actions[i].id);
          availableWidth -= buttonWidths[i];
        } else {
          break;
        }
      }

      setVisibleActions(newVisibleActions);
    };

    const resizeObserver = new ResizeObserver(updateVisibleActions);
    resizeObserver.observe(container);

    // Initial update
    updateVisibleActions();

    return () => {
      resizeObserver.disconnect();
    };
  }, [actions, buttonWidths, isInitialMeasurementDone, actionsLabelWidth]);

  return (
    <header
      className={
        standalone ? "flex flex-col" : "flex flex-col border-b dark:border-muted"
      }
    >
      {actions.length > 0 && (
        <div ref={actionContainerRef} className="flex items-center border-b">
          <span className="mx-2 text-sm text-muted-foreground actions-label">
            Aktionen
          </span>
          {actions.map((action) => (
            <Button
              key={action.id}
              variant="ghost"
              className={cn("px-2 rounded-none action-button", {
                "absolute invisible": !visibleActions.includes(action.id),
              })}
              onClick={action.onClick}
              disabled={action.loading}
            >
              {action.loading ? (
                <Throbber className="w-4 h-4" />
              ) : (
                <action.icon className="w-4 h-4" />
              )}
              <span className="ml-2">{action.label}</span>
            </Button>
          ))}
          {visibleActions.length < actions.length && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-none ml-auto"
                >
                  <EllipsisVertical className="h-5 w-5" />
                  <span className="sr-only">Weitere Aktionen</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {actions
                  .filter((action) => !visibleActions.includes(action.id))
                  .map((action) => (
                    <DropdownMenuItem
                      key={action.id}
                      onClick={action.onClick}
                      disabled={action.loading}
                    >
                      {action.loading ? (
                        <Throbber className="w-4 h-4" />
                      ) : (
                        <action.icon className="w-4 h-4" />
                      )}
                      <span className="ml-2">{action.label}</span>
                    </DropdownMenuItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
      <div className="flex items-center p-4">
        <h2 className="mr-auto ext-xl font-bold flex items-center gap-2">
          <Avatar className="w-10 h-10">
            {isOnline && (
              <span className="absolute right-0 top-0 flex h-3 w-3 rounded-full bg-green-600" />
            )}
            <AvatarFallback>{getInitials(person)}</AvatarFallback>
          </Avatar>
          <div>
            <div>
              {(() => {
                const name =
                  ((person?.givenName ?? person?.familyName) &&
                    `${person?.givenName ?? ""} ${person?.familyName ?? ""}`) ??
                  person?.emailAddress ??
                  "Unbekannt";
                return personHref ? (
                  <Link
                    to={personHref}
                    className="hover:underline text-secondary"
                  >
                    {name}
                  </Link>
                ) : (
                  name
                );
              })()}
              {isOnline && (
                <span className="text-xs text-green-600 block">Online</span>
              )}
            </div>
            {title && (
              <div className="text-sm text-muted-foreground">{title}</div>
            )}
          </div>
        </h2>
        <div className="flex items-center gap-4">
          {connectorHealth && !connectorHealth.isConnected && (
            <Badge
              variant="outline"
              className="text-xs text-destructive border-destructive/40 gap-1"
              title={[
                `Quelle: ${connectorHealth.sourceType}`,
                connectorHealth.connectionStatus &&
                  `Status: ${connectorHealth.connectionStatus}`,
                connectorHealth.lastSyncedAt &&
                  `Zuletzt synchronisiert: ${new Date(connectorHealth.lastSyncedAt).toLocaleString("de-DE")}`,
                connectorHealth.errorMessage &&
                  `Fehler: ${connectorHealth.errorMessage}`,
              ]
                .filter(Boolean)
                .join(" – ")}
            >
              Verbindungsproblem
            </Badge>
          )}
          <div className="flex divide-x divide-border *:px-1">
            {fields.map((info) => (
              <div key={info.id} className="flex flex-col">
                {info.options ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <div className="cursor-pointer hover:bg-accent/50 px-3">
                        <span
                          className="font-semibold text-sm max-w-[144px] hover:max-w-80 truncate transition-all duration-300 ease-in-out block"
                          title={info.value?.trim() ?? "-"}
                        >
                          {info.value?.trim() ?? "-"}
                        </span>
                        <span className="flex items-center text-muted-foreground text-xs shrink-0">
                          {info.icon && <info.icon className="w-4 h-4 mr-2" />}{" "}
                          {info.label}
                        </span>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-[200px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Suchen..." />
                        <CommandList>
                          <CommandEmpty>Keine Optionen gefunden.</CommandEmpty>
                          <CommandGroup>
                            {info.options.map((option) => (
                              <CommandItem
                                key={option.value}
                                value={option.value}
                                // The value is an id; the search matches the label.
                                keywords={[option.label]}
                                onSelect={() => info.onSelect?.(option.value)}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    option.value === info.valueId
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                {option.nested && (
                                  <span className="w-4 shrink-0" />
                                )}
                                {option.team && (
                                  <Users className="mr-1.5 h-4 w-4" />
                                )}
                                <span
                                  className={cn(option.team && "font-semibold")}
                                >
                                  {option.label}
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <div className="px-4 cursor-default">
                    <span
                      className="font-semibold max-w-[144px] truncate block"
                      title={info.value?.trim() ?? "-"}
                    >
                      {info.value?.trim() ?? "-"}
                    </span>
                    <span className="flex items-center text-muted-foreground text-sm shrink-0">
                      {info.icon && <info.icon className="w-4 h-4 mr-2" />}{" "}
                      {info.label}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        {/* Used as pure truthiness here: an empty-string/empty-object value
            should not count as "there is something to show" either. */}
        {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
        {(summary || detailSection1 || detailSection2 || detailSection3) && (
          <>
            <Separator orientation="vertical" />
            <Button
              variant="ghost"
              size="icon"
              className="ml-2"
              onClick={() => setIsInfoExpanded(!isInfoExpanded)}
            >
              {isInfoExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </>
        )}
      </div>

      <div>
        <AnimatePresence>
          {isInfoExpanded && (
            <motion.div
              initial={
                summary || detailSection1 || detailSection2 || detailSection3
                  ? { height: "auto", opacity: 1 }
                  : { height: 0, opacity: 0 }
              }
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="px-6 py-4 grid grid-cols-4 gap-8">
                {(() => {
                  const detailSectionsCount = [
                    detailSection1,
                    detailSection2,
                    detailSection3,
                  ].filter(Boolean).length;

                  return (
                    <>
                      {[detailSection1, detailSection2, detailSection3].map(
                        (section, index) =>
                          section && (
                            <div
                              // Fixed 3-slot tuple in a constant order (never reordered
                              // or data-driven), so positional index is a stable identity.
                              // eslint-disable-next-line @eslint-react/no-array-index-key
                              key={index}
                              className={cn("space-y-2 text-sm group", {
                                "col-span-2": detailSectionsCount === 1,
                              })}
                            >
                              {section.title && (
                                <h3 className="text-sm font-semibold mb-2">
                                  {section.title}
                                </h3>
                              )}
                              {section.entries.map(
                                (entry) =>
                                  entry.value?.trim() && (
                                    <div
                                      key={entry.label ?? entry.value}
                                      className="flex items-center overflow-hidden"
                                      title={entry.hint}
                                    >
                                      <entry.icon className="h-4 w-4 mr-2 text-gray-500 shrink-0" />
                                      {entry.label && (
                                        <>
                                          <span className="font-medium mr-1 shrink-0">
                                            {entry.label}:
                                          </span>
                                        </>
                                      )}
                                      <div className="overflow-hidden">
                                        <div className="line-clamp-2 group-hover:line-clamp-none">
                                          {entry.value}
                                        </div>
                                      </div>
                                    </div>
                                  ),
                              )}
                            </div>
                          ),
                      )}

                      <div
                        className={cn("h-auto", {
                          "col-span-4": detailSectionsCount === 0,
                          "col-span-2": detailSectionsCount <= 2,
                          "col-span-1": detailSectionsCount === 3,
                        })}
                      >
                        <h3 className="text-sm font-semibold mb-2">
                          Zusammenfassung
                        </h3>
                        <div>
                          <p className="text-sm whitespace-pre-line">
                            {summary ?? "Keine Zusammenfassung verfügbar."}
                          </p>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
